import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminQueue() {
  const { 
    members, trainings, reels, reportedContent, reportedMods,
    clubSuggestions, removeClubSuggestion, approveClubSuggestion, CAPITULOS,
    negociosServicios,
    approveMember, rejectMember, approveContent, rejectContent, clearReport, dismissModReport, suspendMember,
    currentUser 
  } = useApp();

  const [reviewingSuggestion, setReviewingSuggestion] = useState(null);
  const [clubForm, setClubForm] = useState(null);
  const [queueError, setQueueError] = useState('');

  // Accessible to Admin, Moderator, AND Collaborator
  if (!['admin', 'moderador', 'colaborador'].includes(currentUser.role)) {
    return <div className="p-20 text-center font-editorial text-2xl text-red-500">Acceso denegado.</div>;
  }

  // Match RLS: members_update_admin vs trainings/reels_update_staff
  const canApproveMembers = currentUser.role === 'admin';
  const canModerateContent = ['moderador', 'admin'].includes(currentUser.role);

  const pendingMembers = members.filter(m => m.status === 'pending');
  const pendingTrainings = trainings.filter(t => t.status === 'pending').map(t => ({ ...t, _type: 'training' }));
  const pendingReels = reels.filter(r => r.status === 'pending').map(r => ({ ...r, _type: 'reel' }));
  const pendingNegocios = (negociosServicios || [])
    .filter((n) => n.status === 'pending' || n.status === 'flagged')
    .map((n) => ({ ...n, _type: 'negocio', titulo: n.nombre }));
  const pendingContent = [...pendingTrainings, ...pendingReels, ...pendingNegocios];
  const pendingClubSuggestions = clubSuggestions.filter((s) => !s.status || s.status === 'pending');

  const handleOpenApproveModal = (suggestion) => {
    setQueueError('');
    setReviewingSuggestion(suggestion);
    setClubForm({
      nombre: suggestion.nombre,
      descripcion: suggestion.descripcion,
      tipo: suggestion.tipo,
      capitulo_id: suggestion.capitulo_id,
      foto_portada: ''
    });
  };

  const handlePublishClub = async (e) => {
    e.preventDefault();
    setQueueError('');
    try {
      const coverUrl = clubForm.foto_portada || 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=800&auto=format&fit=crop';
      await approveClubSuggestion(reviewingSuggestion.id, { ...clubForm, foto_portada: coverUrl });
      setReviewingSuggestion(null);
    } catch (err) {
      console.error('approveClubSuggestion failed:', err);
      setQueueError(err.message || 'No se pudo publicar el club');
    }
  };

  const handleDiscardSuggestion = async (id) => {
    setQueueError('');
    try {
      await removeClubSuggestion(id);
    } catch (err) {
      console.error('removeClubSuggestion failed:', err);
      setQueueError(err.message || 'No se pudo descartar la sugerencia');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
      
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute -inset-x-10 -top-10 h-[200px] bg-[#1B2A4E] -z-10 rounded-b-[100px] sol-watermark" />
        <div className="pt-8 px-4 md:px-8 text-white">
          <span className="label-technical text-[#F6B40E] mb-2 block">Administración</span>
          <h1 className="text-4xl md:text-5xl font-editorial font-bold leading-tight">
            Panel de <span className="text-[#74ACDF] italic">Control.</span>
          </h1>
          <p className="text-white/80 mt-4 max-w-lg">
            Gestión integral de miembros, revisión de contenidos y moderación comunitaria.
          </p>
        </div>
      </div>

      {/* 5. Sugerencias de Clubs (New Section) */}
      <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
        <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
          <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E] flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F6B40E]/10 rounded-xl flex items-center justify-center text-[#F6B40E]">
              <SafeIcon name="Lightbulb" className="w-5 h-5" />
            </div>
            Sugerencias de Clubs ({pendingClubSuggestions.length})
          </h3>
        </div>
        <div className="divide-y divide-stone-100">
          {queueError && (
            <div className="p-4 bg-red-50 text-red-600 text-sm font-medium border-b border-red-100">{queueError}</div>
          )}
          {pendingClubSuggestions.length === 0 ? (
            <div className="p-16 text-center text-stone-500 font-editorial text-xl italic">
              No hay sugerencias de clubs pendientes.
            </div>
          ) : (
            pendingClubSuggestions.map(s => (
              <div key={s.id} className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-stone-50 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-[#1B2A4E]/5 text-[#1B2A4E] rounded-full label-technical text-xs border border-stone-200">
                      Sugerencia - {s.tipo}
                    </span>
                    <span className="text-sm font-medium text-stone-500">
                      Por {s.suggested_by_name} en {CAPITULOS.find(c => c.id === s.capitulo_id)?.nombre}
                    </span>
                  </div>
                  <h4 className="font-bold text-lg text-[#1B2A4E] mb-2">{s.nombre}</h4>
                  <div className="bg-white p-4 rounded-xl text-sm text-stone-600 border border-stone-200 shadow-sm line-clamp-2">
                    {s.descripcion}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
                  <button 
                    onClick={() => handleOpenApproveModal(s)}
                    className="w-full sm:w-auto bg-[#1B2A4E] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#253969] transition-colors shadow-md flex items-center justify-center gap-2"
                  >
                    <SafeIcon name="Edit3" className="w-5 h-5" /> Revisar y Crear
                  </button>
                  <button 
                    onClick={() => handleDiscardSuggestion(s.id)}
                    className="w-full sm:w-auto bg-white border border-red-200 text-red-500 px-6 py-3 rounded-xl text-sm font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon name="X" className="w-5 h-5" /> Descartar
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 1. Miembros Pendientes */}
      <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
        <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
          <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E] flex items-center gap-3">
            <div className="w-10 h-10 bg-[#74ACDF]/10 rounded-xl flex items-center justify-center text-[#74ACDF]">
              <SafeIcon name="UserPlus" className="w-5 h-5" />
            </div>
            Miembros Pendientes ({pendingMembers.length})
          </h3>
        </div>
        <div className="divide-y divide-stone-100">
          {pendingMembers.length === 0 ? (
            <div className="p-16 text-center text-stone-500 font-editorial text-xl italic">
              No hay solicitudes pendientes en este momento.
            </div>
          ) : (
            pendingMembers.map(m => (
              <div key={m.id} className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-stone-50 transition-colors">
                <div className="flex items-center gap-5">
                  <img src={m.foto_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(m.nombre || '?')}`} alt="" className="w-16 h-16 rounded-full object-cover ring-4 ring-stone-100" />
                  <div>
                    <h4 className="font-bold text-lg text-[#1B2A4E]">{m.nombre}</h4>
                    <p className="label-technical text-[#74ACDF] mt-1">{m.rubro}</p>
                  </div>
                </div>
                {canApproveMembers && (
                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                    <button 
                      onClick={async () => {
                        try {
                          await approveMember(m.id);
                        } catch (err) {
                          console.error(err);
                          setQueueError(err.message || 'No se pudo aprobar el miembro');
                        }
                      }}
                      className="w-full sm:w-auto bg-[#25D366] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#20bd5a] transition-colors shadow-md flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="Check" className="w-5 h-5" /> Aprobar
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await rejectMember(m.id);
                        } catch (err) {
                          console.error(err);
                          setQueueError(err.message || 'No se pudo rechazar el miembro');
                        }
                      }}
                      className="w-full sm:w-auto bg-white border border-red-200 text-red-500 px-6 py-3 rounded-xl text-sm font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="X" className="w-5 h-5" /> Rechazar
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 2. Contenido Pendiente */}
      <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
        <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
          <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E] flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F6B40E]/10 rounded-xl flex items-center justify-center text-[#F6B40E]">
              <SafeIcon name="PlayCircle" className="w-5 h-5" />
            </div>
            Contenido Pendiente ({pendingContent.length})
          </h3>
        </div>
        <div className="divide-y divide-stone-100">
          {pendingContent.length === 0 ? (
            <div className="p-16 text-center text-stone-500 font-editorial text-xl italic">
              Todo el contenido ha sido revisado.
            </div>
          ) : (
            pendingContent.map(item => {
              const author = members.find(m => m.id === (
                item.presentado_por_member_id || item.member_id || item.submitted_by
              ));
              const typeLabel =
                item._type === 'training' ? 'Capacitación'
                  : item._type === 'reel' ? 'Reel'
                    : item._type === 'negocio'
                      ? (item.status === 'flagged' ? 'Negocio/Servicio · Reportado' : 'Negocio/Servicio')
                      : item._type;
              const typeBadgeClass =
                item._type === 'training' ? 'bg-[#1B2A4E]/10 text-[#1B2A4E]'
                  : item._type === 'reel' ? 'bg-[#74ACDF]/10 text-[#74ACDF]'
                    : item.status === 'flagged' ? 'bg-orange-50 text-orange-700'
                      : 'bg-[#F6B40E]/15 text-[#1B2A4E]';
              return (
                <div key={`${item._type}-${item.id}`} className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-stone-50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full label-technical text-xs ${typeBadgeClass}`}>
                        {typeLabel}
                      </span>
                      <span className="text-sm font-medium text-stone-500 flex items-center gap-1.5">
                        <SafeIcon name="User" className="w-3.5 h-3.5" /> por {author?.nombre || item.presentado_por_externo || 'Miembro'}
                      </span>
                    </div>
                    <h4 className="font-bold text-lg text-[#1B2A4E] leading-tight">{item.titulo}</h4>
                    {item._type === 'negocio' && item.categoria && (
                      <p className="text-sm text-stone-500 mt-1">{item.categoria}</p>
                    )}
                  </div>
                  {canModerateContent && (
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
                      <button 
                        onClick={async () => { try { await approveContent(item._type, item.id); } catch (err) { console.error(err); } }}
                        className="w-full sm:w-auto bg-[#25D366] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#20bd5a] transition-colors shadow-md flex items-center justify-center gap-2"
                      >
                        <SafeIcon name="Check" className="w-5 h-5" /> Aprobar
                      </button>
                      <button 
                        onClick={async () => { try { await rejectContent(item._type, item.id); } catch (err) { console.error(err); } }}
                        className="w-full sm:w-auto bg-white border border-red-200 text-red-500 px-6 py-3 rounded-xl text-sm font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <SafeIcon name="X" className="w-5 h-5" /> Rechazar
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 3. Contenido Reportado */}
      <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
        <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
          <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E] flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-500">
              <SafeIcon name="AlertTriangle" className="w-5 h-5" />
            </div>
            Contenido Reportado ({reportedContent.length})
          </h3>
        </div>
        <div className="divide-y divide-stone-100">
          {reportedContent.length === 0 ? (
            <div className="p-16 text-center text-stone-500 font-editorial text-xl italic">
              No hay reportes de contenido activos.
            </div>
          ) : (
            reportedContent.map(report => (
              <div key={report.id} className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-stone-50 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 rounded-full label-technical text-xs bg-red-50 text-red-600">
                      Reporte
                    </span>
                    <span className="text-sm font-medium text-stone-500">
                      por {report.reported_by_name}
                    </span>
                  </div>
                  <h4 className="font-bold text-lg text-[#1B2A4E] mb-2">"{report.titulo}"</h4>
                  <div className="bg-stone-100 p-4 rounded-xl text-sm text-stone-600 italic border border-stone-200">
                    Motivo: {report.reason}
                  </div>
                </div>
                {canModerateContent && (
                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
                    <button 
                      onClick={async () => {
                        try {
                          await clearReport(report.id);
                        } catch (err) {
                          console.error(err);
                          setQueueError(err.message || 'No se pudo ignorar el reporte');
                        }
                      }}
                      className="w-full sm:w-auto bg-stone-100 text-stone-600 px-6 py-3 rounded-xl text-sm font-bold hover:bg-stone-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="CheckCircle" className="w-5 h-5" /> Ignorar
                    </button>
                    <button 
                      onClick={async () => {
                        try {
                          await rejectContent(report.type, report.content_id);
                        } catch (err) {
                          console.error(err);
                          setQueueError(err.message || 'No se pudo eliminar el contenido');
                        }
                      }}
                      className="w-full sm:w-auto bg-white border border-red-200 text-red-500 px-6 py-3 rounded-xl text-sm font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="Trash2" className="w-5 h-5" /> Eliminar Contenido
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 4. Moderadores Reportados */}
      {currentUser.role === 'admin' && (
        <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
          <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
            <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E] flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1B2A4E] rounded-xl flex items-center justify-center text-white">
                <SafeIcon name="ShieldAlert" className="w-5 h-5" />
              </div>
              Moderadores Reportados ({reportedMods.length})
            </h3>
          </div>
          <div className="divide-y divide-stone-100">
            {reportedMods.length === 0 ? (
              <div className="p-16 text-center text-stone-500 font-editorial text-xl italic">
                El equipo de moderación funciona sin incidencias.
              </div>
            ) : (
              reportedMods.map(report => (
                <div key={report.id} className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-stone-50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-3 py-1 bg-[#1B2A4E] text-white rounded-full label-technical text-xs flex items-center gap-1">
                        <SafeIcon name="Shield" className="w-3 h-3" /> Mod Flag
                      </span>
                      <span className="text-sm font-medium text-stone-500">
                        Elevado por {report.reported_by_name}
                      </span>
                    </div>
                    <h4 className="font-bold text-lg text-[#1B2A4E] mb-2">{report.moderator_name}</h4>
                    <div className="bg-red-50 p-4 rounded-xl text-sm text-red-800 border border-red-100 italic">
                      Motivo: {report.reason}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
                    <button 
                      onClick={async () => {
                        try {
                          await dismissModReport(report.id);
                        } catch (err) {
                          console.error(err);
                          setQueueError(err.message || 'No se pudo descartar el flag');
                        }
                      }}
                      className="w-full sm:w-auto bg-stone-100 text-stone-600 px-6 py-3 rounded-xl text-sm font-bold hover:bg-stone-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="CheckCircle" className="w-5 h-5" /> Descartar
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await suspendMember(report.id);
                        } catch (err) {
                          console.error(err);
                          setQueueError(err.message || 'No se pudo suspender');
                        }
                      }}
                      className="w-full sm:w-auto bg-red-500 text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-red-600 transition-colors shadow-md flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="UserX" className="w-5 h-5" /> Suspender
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Modal: Review Suggestion & Create Club */}
      <AnimatePresence>
        {reviewingSuggestion && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setReviewingSuggestion(null)} className="absolute inset-0 bg-[#1B2A4E]/60 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-xl relative z-10 overflow-hidden border border-stone-100"
            >
              <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
                <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Aprobar Club Sugerido</h2>
                <p className="text-sm text-stone-500 mt-1">Revisa y ajusta los detalles antes de publicarlo oficialmente.</p>
              </div>
              <form onSubmit={handlePublishClub} className="p-8 space-y-5">
                {queueError && (
                  <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{queueError}</div>
                )}
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Nombre del Club</label>
                  <input required type="text" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={clubForm.nombre} onChange={e => setClubForm({...clubForm, nombre: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Descripción</label>
                  <textarea required rows="3" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={clubForm.descripcion} onChange={e => setClubForm({...clubForm, descripcion: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Tipo</label>
                    <select className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={clubForm.tipo} onChange={e => setClubForm({...clubForm, tipo: e.target.value})}>
                      <option>Mateada</option>
                      <option>Asado</option>
                      <option>Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Capítulo</label>
                    <select required className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={clubForm.capitulo_id} onChange={e => setClubForm({...clubForm, capitulo_id: e.target.value})}>
                      {CAPITULOS.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">URL de Foto de Portada (Opcional)</label>
                  <input type="url" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-sm text-stone-500" placeholder="https://..." value={clubForm.foto_portada} onChange={e => setClubForm({...clubForm, foto_portada: e.target.value})} />
                </div>
                <div className="pt-4 flex justify-end gap-3 border-t border-stone-100 mt-6">
                  <button type="button" onClick={() => setReviewingSuggestion(null)} className="px-6 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit" className="px-8 py-3 rounded-xl font-bold bg-[#1B2A4E] text-white hover:bg-[#253969] transition-colors shadow-md">
                    Publicar Club
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </motion.div>
  );
}